import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useNotification } from '../context/NotificationContext';
import { useUsers } from '../context/UsersContext';
import { useClarificationTypes } from '../hooks/useMasterData';
import SearchableDropdown from '../components/SearchableDropdown';
import USDateInput from '../components/USDateInput';
import { filterEmojis } from '../utils/emojiFilter';
import './CreateDrl.css';

const CreateDrl = () => {
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const { users } = useUsers();
  const { data: clarificationTypes, loading: clarificationTypesLoading } = useClarificationTypes(true);
  const [loading, setLoading] = useState(false);

  // Debug: Log users array
  useEffect(() => {
    console.log('CreateDrl - Users loaded:', users?.length || 0, users);
  }, [users]);
  const [practices, setPractices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [formData, setFormData] = useState({
    practiceCode: '',
    locationCode: '',
    drlReceivedDate: '',
    totalDrlAmount: '',
    creditCardAmount: '',
    paperCheckAmount: '',
    eCheckAmount: '',
    assigneeId: '',
    reporteeId: '',
    drlNotes: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [error, setError] = useState('');
  const [clarifications, setClarifications] = useState([]);
  const [showAddClarification, setShowAddClarification] = useState(false);
  const [showClarificationSection, setShowClarificationSection] = useState(false);
  const [newClarification, setNewClarification] = useState({
    clarificationType: '',
    details: '',
    assignee: '', // ONSHORE or OFFSHORE
    assigneeId: '', // User ID
    reportee: '', // ONSHORE or OFFSHORE
    reporterId: '', // User ID
    status: 'OPEN',
    amount: ''
  });

  useEffect(() => {
    fetchPractices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (formData.practiceCode && practices.length > 0) {
      fetchLocations(formData.practiceCode);
    } else {
      setLocations([]);
      setFormData(prev => ({ ...prev, locationCode: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.practiceCode, practices]);

  const fetchPractices = async () => {
    try {
      const response = await api.getAllPractices();
      const practicesList = Array.isArray(response) ? response : (response?.items || []);
      setPractices(practicesList.filter(p => p.isActive !== false));
    } catch (error) {
      console.error('Error fetching practices:', error);
      const errorMessage = error?.message || error?.data?.message || 'Failed to fetch practices';
      showError(errorMessage);
    }
  };

  const fetchLocations = async (practiceCode) => {
    try {
      const practice = practices.find(p => p.code === practiceCode);
      if (practice && (practice.practiceId || practice.id)) {
        const practiceId = practice.practiceId || practice.id;
        const response = await api.getPracticeLocations(practiceId);
        const locationsList = Array.isArray(response) ? response : (response?.items || []);
        setLocations(locationsList.filter(l => l.isActive !== false));
      } else {
        setLocations([]);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      const errorMessage = error?.message || error?.data?.message || 'Failed to fetch locations';
      showError(errorMessage);
      setLocations([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Filter emojis from string inputs
    const filteredValue = filterEmojis(value);
    setFormData(prev => ({
      ...prev,
      [name]: filteredValue
    }));
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    setError('');
  };

  const handleAmountChange = (e) => {
    const { name, value } = e.target;
    // Allow only numbers and decimal point
    const sanitizedValue = value.replace(/[^\d.]/g, '');
    // Ensure only one decimal point
    const parts = sanitizedValue.split('.');
    const formattedValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : sanitizedValue;
    
    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }));
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    setError('');
  };


  const handleDropdownChange = (name, value) => {
    console.log('Main form dropdown change:', name, value);
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: value
      };
      console.log('Updated main form state:', updated);
      return updated;
    });
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    setError('');
  };

  const handleAddClarification = () => {
    setShowAddClarification(true);
  };

  const handleClarificationChange = (e) => {
    const { name, value } = e.target;
    const filteredValue = filterEmojis(value);
    
    // Auto-select opposite type for reporter when assignee is selected
    if (name === 'assignee') {
      const oppositeType = value === 'ONSHORE' ? 'OFFSHORE' : value === 'OFFSHORE' ? 'ONSHORE' : '';
      setNewClarification(prev => ({
        ...prev,
        [name]: filteredValue,
        reportee: oppositeType
      }));
    }
    // Auto-select opposite type for assignee when reporter is selected
    else if (name === 'reportee') {
      const oppositeType = value === 'ONSHORE' ? 'OFFSHORE' : value === 'OFFSHORE' ? 'ONSHORE' : '';
      setNewClarification(prev => ({
        ...prev,
        [name]: filteredValue,
        assignee: oppositeType
      }));
    }
    else {
      setNewClarification(prev => ({
        ...prev,
        [name]: filteredValue
      }));
    }
  };

  const handleClarificationDropdownChange = (name, value) => {
    console.log('Clarification dropdown change:', name, value);
    console.log('Current newClarification before update:', newClarification);
    setNewClarification(prev => {
      const updated = {
        ...prev,
        [name]: value
      };
      console.log('Updated clarification state:', updated);
      return updated;
    });
  };

  const handleSaveClarification = () => {
    // Debug logging
    console.log('Saving clarification:', newClarification);
    console.log('Available users:', users);
    
    // Validate clarification
    if (!newClarification.clarificationType || !newClarification.details || 
        !newClarification.assignee || !newClarification.assigneeId || 
        !newClarification.reportee || !newClarification.reporterId) {
      showError('Please fill in all clarification fields');
      return;
    }

    // Get user names for display - handle both userId and id fields
    const assigneeUser = users.find(u => (u.userId || u.id) === newClarification.assigneeId);
    const reporterUser = users.find(u => (u.userId || u.id) === newClarification.reporterId);
    
    console.log('Found assigneeUser:', assigneeUser);
    console.log('Found reporterUser:', reporterUser);

    // Add clarification to list
    setClarifications(prev => [...prev, {
      ...newClarification,
      assigneeName: assigneeUser 
        ? (assigneeUser.firstName && assigneeUser.lastName 
          ? `${assigneeUser.firstName} ${assigneeUser.lastName}`
          : assigneeUser.email || 'Unknown User')
        : 'N/A',
      reporteeName: reporterUser 
        ? (reporterUser.firstName && reporterUser.lastName 
          ? `${reporterUser.firstName} ${reporterUser.lastName}`
          : reporterUser.email || 'Unknown User')
        : 'N/A',
      id: Date.now() // Temporary ID for UI
    }]);

    // Reset form
    setNewClarification({
      clarificationType: '',
      details: '',
      assignee: '',
      assigneeId: '',
      reportee: '',
      reporterId: '',
      status: 'OPEN',
      amount: ''
    });
    setShowAddClarification(false);
    showSuccess('Clarification added');
  };

  const handleCancelClarification = () => {
    setNewClarification({
      clarificationType: '',
      details: '',
      assignee: '',
      assigneeId: '',
      reportee: '',
      reporterId: '',
      status: 'OPEN',
      amount: ''
    });
    setShowAddClarification(false);
  };

  const handleRemoveClarification = (id) => {
    setClarifications(prev => prev.filter(c => c.id !== id));
    showSuccess('Clarification removed');
  };

  // Check if amounts match
  const checkAmountsMatch = () => {
    const totalDrlAmount = parseFloat(formData.totalDrlAmount) || 0;
    const creditCardAmount = parseFloat(formData.creditCardAmount) || 0;
    const paperCheckAmount = parseFloat(formData.paperCheckAmount) || 0;
    const eCheckAmount = parseFloat(formData.eCheckAmount) || 0;
    const sumOfAmounts = creditCardAmount + paperCheckAmount + eCheckAmount;
    
    // If total is 0 or not entered, consider it as matching (no error)
    if (!formData.totalDrlAmount || totalDrlAmount === 0) {
      return true;
    }
    
    return Math.abs(totalDrlAmount - sumOfAmounts) <= 0.01;
  };

  const validateForm = () => {
    const errors = {};

    // Required fields
    if (!formData.practiceCode) errors.practiceCode = 'Practice is required';
    if (!formData.locationCode) errors.locationCode = 'Location is required';
    if (!formData.drlReceivedDate) errors.drlReceivedDate = 'DRL Received Date is required';

    // Amount validation - check if amounts don't match
    if (!checkAmountsMatch()) {
      // Show clarification section
      setShowClarificationSection(true);
      
      // Check if clarification has been added
      if (clarifications.length === 0) {
        showError('Total amount does not match. Please add a clarification before creating the DRL.');
        errors.amountMismatch = true;
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Prepare DRL data
      const drlData = {
        practiceCode: formData.practiceCode,
        locationCode: formData.locationCode,
        drlReceivedDate: formData.drlReceivedDate,
        totalDrlAmount: parseFloat(formData.totalDrlAmount) || 0,
        creditCardAmount: parseFloat(formData.creditCardAmount) || 0,
        paperCheckAmount: parseFloat(formData.paperCheckAmount) || 0,
        eCheckAmount: parseFloat(formData.eCheckAmount) || 0,
        checksAmount: 0,
        otherAmount: 0,
        onshorePostingAmount: 0,
        offshorePostingAmount: 0,
        remainingAmount: 0,
        interestAmount: 0,
        nonArAmount: 0,
        miscellaneousAmount: 0,
        medicalRecordFee: 0
      };

      // Add optional fields if provided
      if (formData.assigneeId) {
        drlData.assigneeId = formData.assigneeId;
      }
      if (formData.reporteeId) {
        drlData.reporteeId = formData.reporteeId;
      }
      if (formData.drlNotes && formData.drlNotes.trim() !== '') {
        drlData.drlNotes = formData.drlNotes.trim();
      }

      // Add clarifications if any
      if (clarifications.length > 0) {
        drlData.clarifications = clarifications.map(c => {
          const cl = {
            clarificationType: c.clarificationType,
            details: c.details,
            assignee: c.assignee,
            assigneeId: c.assigneeId,
            reportee: c.reportee,
            reporterId: c.reporterId,
            status: c.status || 'OPEN'
          };
          if (c.amount != null && c.amount !== '') {
            cl.amount = c.amount;
          }
          return cl;
        });
      }

      const response = await api.createDrl(drlData);
      
      showSuccess('DRL created successfully');
      
      // Navigate to DRL details page or back to DRL listing
      if (response.drlId) {
        navigate(`/dit-drl/${response.drlId}`);
      } else {
        navigate('/dit-drl');
      }
    } catch (err) {
      console.error('Error creating DRL:', err);
      const errorMessage = err?.message || err?.data?.message || 'Failed to create DRL. Please try again.';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Navigate back to the DRL listing page
    navigate('/dit-drl');
  };

  // Prepare user options for dropdowns
  const userOptions = [
    { value: '', label: 'Select User' },
    ...(users || []).map(user => ({
      value: user.userId || user.id,
      label: user.firstName && user.lastName
        ? `${user.firstName} ${user.lastName}`
        : user.email || 'Unknown User'
    }))
  ];

  // Prepare practice options
  const practiceOptions = [
    { value: '', label: 'Select Practice' },
    ...(practices || []).map(practice => ({
      value: practice.code,
      label: `${practice.code} - ${practice.name}`
    }))
  ];

  // Prepare location options
  const locationOptions = [
    { value: '', label: 'Select Location' },
    ...(locations || []).map(location => ({
      value: location.code,
      label: `${location.code} - ${location.name}`
    }))
  ];

  return (
    <div className="create-drl-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1 className="page-title">Create DRL</h1>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <form className="create-drl-form" onSubmit={handleSubmit}>
        <div className="form-sections-container">
          {/* DRL Information Section */}
          <div className="form-section">
            <h3 className="section-title">DRL Information</h3>
            <div className="form-grid form-grid-three-columns">
              <div className="form-group">
                <label>
                  Practice <span className="required">*</span>
                </label>
                <SearchableDropdown
                  options={practiceOptions}
                  value={formData.practiceCode}
                  onChange={(value) => handleDropdownChange('practiceCode', value)}
                  placeholder="Select Practice"
                  maxVisibleItems={5}
                  className={formErrors.practiceCode ? 'error' : ''}
                />
                {formErrors.practiceCode && (
                  <span className="error-text">{formErrors.practiceCode}</span>
                )}
              </div>

              <div className="form-group">
                <label>
                  Location <span className="required">*</span>
                </label>
                <SearchableDropdown
                  options={locationOptions}
                  value={formData.locationCode}
                  onChange={(value) => handleDropdownChange('locationCode', value)}
                  placeholder="Select Location"
                  maxVisibleItems={5}
                  className={formErrors.locationCode ? 'error' : ''}
                  disabled={!formData.practiceCode}
                />
                {formErrors.locationCode && (
                  <span className="error-text">{formErrors.locationCode}</span>
                )}
              </div>

              <div className="form-group">
                <label>
                  DRL Received Date <span className="required">*</span>
                </label>
                <USDateInput
                  id="drlReceivedDate"
                  name="drlReceivedDate"
                  value={formData.drlReceivedDate}
                  onChange={handleInputChange}
                  placeholder="MM/DD/YYYY"
                  className={formErrors.drlReceivedDate ? 'error' : ''}
                />
                {formErrors.drlReceivedDate && (
                  <span className="error-text">{formErrors.drlReceivedDate}</span>
                )}
              </div>
            </div>
          </div>

          {/* Amount Information Section */}
          <div className="form-section">
            <div className="form-grid form-grid-four-columns">
              <div className="form-group">
                <label>Total DRL Amount</label>
                <input
                  type="text"
                  name="totalDrlAmount"
                  value={formData.totalDrlAmount}
                  onChange={handleAmountChange}
                  placeholder="0.00"
                />
              </div>

              <div className="form-group">
                <label>Paper Check Amount</label>
                <input
                  type="text"
                  name="paperCheckAmount"
                  value={formData.paperCheckAmount}
                  onChange={handleAmountChange}
                  placeholder="0.00"
                />
              </div>

              <div className="form-group">
                <label>Credit Card Amount</label>
                <input
                  type="text"
                  name="creditCardAmount"
                  value={formData.creditCardAmount}
                  onChange={handleAmountChange}
                  placeholder="0.00"
                />
              </div>

              <div className="form-group">
                <label>eCheck Amount</label>
                <input
                  type="text"
                  name="eCheckAmount"
                  value={formData.eCheckAmount}
                  onChange={handleAmountChange}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Assignment Information Section */}
          <div className="form-section">
            <div className="form-grid form-grid-two-columns">
              <div className="form-group">
                <label>Assignee Name</label>
                <SearchableDropdown
                  options={userOptions}
                  value={formData.assigneeId}
                  onChange={(value) => handleDropdownChange('assigneeId', value)}
                  placeholder="Select Assignee"
                  maxVisibleItems={5}
                />
              </div>

              <div className="form-group">
                <label>Reporter Name</label>
                <SearchableDropdown
                  options={userOptions}
                  value={formData.reporteeId}
                  onChange={(value) => handleDropdownChange('reporteeId', value)}
                  placeholder="Select Reporter"
                  maxVisibleItems={5}
                />
              </div>
            </div>

          </div>

          {/* Notes Section - Full Row */}
          <div className="form-section">
            <div className="form-group">
              <label>Notes</label>
              <textarea
                name="drlNotes"
                value={formData.drlNotes}
                onChange={handleInputChange}
                placeholder="Enter any additional notes..."
                rows={1}
                style={{ width: '100%', resize: 'vertical' }}
              />
            </div>
          </div>

          {/* Clarifications Section - Only show after user tries to submit with mismatched amounts */}
          {showClarificationSection && (
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Clarifications</h3>
                {!showAddClarification && (
                  <button
                    type="button"
                    className="btn-add-clarification"
                    onClick={handleAddClarification}
                  >
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                      <path d="M10 5V15M5 10H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Add Clarification
                  </button>
                )}
              </div>

            {/* Existing Clarifications */}
            {clarifications.length > 0 && (
              <div className="clarifications-list">
                {clarifications.map((clarification, index) => (
                  <div key={clarification.id} className="clarification-item">
                    <div className="clarification-header">
                      <span className="clarification-number">Clarification #{index + 1}</span>
                      <button
                        type="button"
                        className="btn-remove-clarification"
                        onClick={() => handleRemoveClarification(clarification.id)}
                        title="Remove clarification"
                      >
                        <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                          <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                    <div className="clarification-details">
                      <div className="clarification-field">
                        <span className="field-label">Type</span>
                        <span className="field-value">{clarification.clarificationType}</span>
                      </div>
                      <div className="clarification-field">
                        <span className="field-label">Status</span>
                        <span className="status-badge status-open">{clarification.status}</span>
                      </div>
                      {clarification.amount != null && clarification.amount !== '' && (
                        <div className="clarification-field">
                          <span className="field-label">Amount</span>
                          <span className="field-value">{clarification.amount}</span>
                        </div>
                      )}
                      <div className="clarification-field">
                        <span className="field-label">Assignee</span>
                        <span className="field-value">{clarification.assignee}</span>
                      </div>
                      <div className="clarification-field">
                        <span className="field-label">Assignee Name</span>
                        <span className="field-value">{clarification.assigneeName}</span>
                      </div>
                      <div className="clarification-field">
                        <span className="field-label">Reporter</span>
                        <span className="field-value">{clarification.reportee}</span>
                      </div>
                      <div className="clarification-field">
                        <span className="field-label">Reporter Name</span>
                        <span className="field-value">{clarification.reporteeName}</span>
                      </div>
                      <div className="clarification-field full-width">
                        <span className="field-label">Details</span>
                        <span className="field-value">{clarification.details}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add New Clarification Form */}
            {showAddClarification && (
              <div className="add-clarification-card">
                <h4 className="card-title">Add New Clarification</h4>
                
                {/* First Row: Type, Status, and Amount */}
                <div className="form-grid form-grid-three-columns">
                  <div className="form-group">
                    <label>
                      Clarification Type <span className="required">*</span>
                    </label>
                    <SearchableDropdown
                      options={[
                        { value: '', label: 'Select Clarification Type' },
                        ...clarificationTypes.map(ct => ({
                          value: ct.code,
                          label: ct.name
                        }))
                      ]}
                      value={newClarification.clarificationType}
                      onChange={(value) => setNewClarification(prev => ({ ...prev, clarificationType: value }))}
                      placeholder="Select Clarification Type"
                      maxVisibleItems={5}
                      disabled={clarificationTypesLoading}
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Status <span className="required">*</span>
                    </label>
                    <select
                      name="status"
                      value={newClarification.status}
                      onChange={handleClarificationChange}
                    >
                      <option value="OPEN">Open</option>
                      <option value="RESOLVED">Resolved</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Amount (Optional)</label>
                    <input
                      type="number"
                      name="amount"
                      step="0.01"
                      value={newClarification.amount || ''}
                      onChange={handleClarificationChange}
                      placeholder="Enter amount"
                    />
                  </div>
                </div>

                {/* Second Row: Assignee, Reporter, Assignee Name, Reporter Name (4 columns) */}
                <div className="form-grid form-grid-four-columns" style={{ marginTop: '8px' }}>
                  <div className="form-group">
                    <label>
                      Assignee <span className="required">*</span>
                    </label>
                    <select
                      name="assignee"
                      value={newClarification.assignee}
                      onChange={handleClarificationChange}
                    >
                      <option value="">Select Type</option>
                      <option value="ONSHORE">Onshore</option>
                      <option value="OFFSHORE">Offshore</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>
                      Reporter <span className="required">*</span>
                    </label>
                    <select
                      name="reportee"
                      value={newClarification.reportee}
                      onChange={handleClarificationChange}
                    >
                      <option value="">Select Type</option>
                      <option value="ONSHORE">Onshore</option>
                      <option value="OFFSHORE">Offshore</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>
                      Assignee Name <span className="required">*</span>
                    </label>
                    <SearchableDropdown
                      options={userOptions}
                      value={newClarification.assigneeId}
                      onChange={(value) => handleClarificationDropdownChange('assigneeId', value)}
                      placeholder="Select Assignee"
                      maxVisibleItems={5}
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Reporter Name <span className="required">*</span>
                    </label>
                    <SearchableDropdown
                      options={userOptions}
                      value={newClarification.reporterId}
                      onChange={(value) => handleClarificationDropdownChange('reporterId', value)}
                      placeholder="Select Reporter"
                      maxVisibleItems={5}
                    />
                  </div>
                </div>

                {/* Third Row: Details (full width) */}
                <div className="form-grid" style={{ marginTop: '8px' }}>
                  <div className="form-group full-width">
                    <label>
                      Details <span className="required">*</span>
                    </label>
                    <textarea
                      name="details"
                      value={newClarification.details}
                      onChange={handleClarificationChange}
                      placeholder="Enter clarification details"
                      rows={3}
                    />
                  </div>
                </div>

                <div className="clarification-actions">
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={handleCancelClarification}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn-save-clarification"
                    onClick={handleSaveClarification}
                  >
                    Save Clarification
                  </button>
                </div>
              </div>
            )}
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-submit"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create DRL'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateDrl;
